import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hasBillingRole } from '../utils/roles';
import { Link } from 'react-router-dom';
import { LuRefreshCw, LuFileText, LuCircleAlert } from 'react-icons/lu';
import LoadingSpinner from './LoadingSpinner';
import { toast } from 'react-toastify';

const CrossDockBillingManager = ({ request, onInvoiceCreated, readOnly = false }) => {
  // readOnly distinguishes customers; internal users additionally need
  // the billing role to create invoices (RF15).
  const { user } = useAuth();
  const canMutateBilling = hasBillingRole(user);
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const fetchBillingSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/crossdock/requests/${request.id}/billing/summary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to fetch billing summary');
      setBillingData(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processBilling = async () => {
    try {
      setProcessing(true);
      const response = await fetch(`/api/crossdock/requests/${request.id}/billing/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to process billing');
      const data = await response.json();
      if (data.success) {
        toast.success('Invoice created');
        if (onInvoiceCreated && data.invoice) onInvoiceCreated(data.invoice);
        fetchBillingSummary();
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (request?.id) fetchBillingSummary();
  }, [request?.id]);

  if (loading) return <div className="flex justify-center py-8"><LoadingSpinner /></div>;

  if (error) {
    return (
      <div className="text-center py-8 text-gray-500">
        <LuCircleAlert className="mx-auto h-8 w-8 text-gray-300 mb-2" />
        <p className="text-sm">{error}</p>
        <button onClick={fetchBillingSummary} className="mt-2 text-sm text-indigo-600 hover:text-indigo-800">Retry</button>
      </div>
    );
  }

  if (!billingData) {
    return <div className="text-center py-8 text-sm text-gray-500">No billing data available.</div>;
  }

  const { billingItems, subtotal: backendSubtotal, totalAmount, tax, invoice } = billingData;
  const subtotal = backendSubtotal || totalAmount || 0;
  const taxInfo = tax || { name: 'GST', rate: 0.05, amount: subtotal * 0.05 };
  const taxAmount = taxInfo.amount || 0;
  const total = totalAmount || (subtotal + taxAmount);

  // Check for connected invoice. Cancelled invoices are HISTORY, not a
  // connection -- treating Invoices[0] as connected regardless of
  // status hid the Create Invoice button after a cancellation, so the
  // backend's re-bill path (which ignores cancelled invoices) was
  // unreachable from the UI.
  // readOnly marks the customer mount of this shared component --
  // invoice links must route into /customer, not /admin (matching
  // RequestBillingManager's isAdmin split).
  const requestInvoices = request?.Invoices || [];
  const connectedInvoice = invoice
    || requestInvoices.find((inv) => inv.status !== 'cancelled')
    || null;
  const cancelledInvoices = requestInvoices.filter((inv) => inv.status === 'cancelled');

  const getInvoiceStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      draft: 'bg-gray-100 text-gray-600'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div>
      {/* Invoice-style billing breakdown */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {/* Line items */}
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase w-20">Qty</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase w-24">Rate</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase w-28">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {billingItems && billingItems.length > 0 ? (
              billingItems.map((item, index) => {
                const rate = item.unit_price || item.rate || 0;
                const amount = item.total_amount || item.amount || (rate * (item.quantity || 0));
                return (
                  <tr key={index}>
                    <td className="px-6 py-3 text-sm text-gray-900">
                      <div>{item.description || item.name}</div>
                      <div className="text-xs text-gray-400">{(item.billing_unit || '').replace(/_/g, ' ')}</div>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700 text-right">{item.quantity}</td>
                    <td className="px-6 py-3 text-sm text-gray-700 text-right">${rate.toFixed(2)}</td>
                    <td className="px-6 py-3 text-sm font-medium text-gray-900 text-right">${amount.toFixed(2)}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                  No billable items. Customer may not have a fee schedule configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Totals */}
        {billingItems && billingItems.length > 0 && (
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900 font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{taxInfo.name} ({(taxInfo.rate * 100).toFixed(0)}%)</span>
                  <span className="text-gray-900">${(taxAmount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold border-t border-gray-300 pt-2">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invoice status / actions */}
      <div className="mt-6 flex items-center justify-between">
        <div>
          {connectedInvoice ? (
            <div className="flex items-center space-x-3">
              <Link
                to={readOnly ? `/customer/invoices/${connectedInvoice.id}` : `/admin/invoices/${connectedInvoice.id}`}
                className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800"
              >
                <LuFileText className="mr-1 h-4 w-4" />
                Invoice {connectedInvoice.invoiceNumber || connectedInvoice.id?.substring(0, 8)}
              </Link>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getInvoiceStatusBadge(connectedInvoice.status)}`}>
                {(connectedInvoice.status || 'draft').charAt(0).toUpperCase() + (connectedInvoice.status || 'draft').slice(1)}
              </span>
            </div>
          ) : billingItems && billingItems.length > 0 ? (
            <p className="text-sm text-gray-500">No invoice created yet</p>
          ) : null}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchBillingSummary}
            disabled={loading}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <LuRefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Refresh
          </button>
          {!readOnly && canMutateBilling && !connectedInvoice && billingItems && billingItems.length > 0 && (
            <button
              onClick={processBilling}
              disabled={processing}
              className="inline-flex items-center px-4 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {processing ? 'Creating...' : 'Create Invoice'}
            </button>
          )}
        </div>
      </div>
      {cancelledInvoices.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-2">
          <p className="text-xs text-gray-500 mb-1">Cancelled invoices (history):</p>
          {cancelledInvoices.map((inv) => (
            <div key={inv.id} className="flex items-center gap-2 text-xs text-gray-500">
              <Link to={readOnly ? `/customer/invoices/${inv.id}` : `/admin/invoices/${inv.id}`} className="underline hover:text-gray-700">
                Invoice {inv.invoiceNumber || inv.id?.substring(0, 8)}
              </Link>
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-medium ${getInvoiceStatusBadge('cancelled')}`}>
                Cancelled
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CrossDockBillingManager;
